// Business identifiers (GSTIN, IEC, RCMC, GeM, CIN, Udyam, BIS, EPR, PCB consent, ABN …): badges, the
// "Business registrations" panel on a profile, the read-only list on a company page, and registration fields.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, ExternalLink, Plus, ShieldAlert, Trash2 } from 'lucide-react';
import { Badge, Empty, ErrorNotice, Field, Loading, Modal, Notice, Panel } from '../../components/ui';
import { identifiers as api, type IdentifierInput } from '../../lib/trade';
import type { Identifier, IdentifierScheme, IdentifierStatus } from '../../types/trade';

const STATUS_LABEL: Record<IdentifierStatus, string> = {
  declared: 'Not yet checked', matched: 'Checked on public register', documented: 'Certificate checked', rejected: 'Rejected',
};

export function useSchemes(country?: string) {
  return useQuery({ queryKey: ['identifier-schemes', country ?? ''], queryFn: ({ signal }) => api.schemes(country, signal), staleTime: 3_600_000 });
}

/** Verified registrations as small green chips, e.g. "GST verified · IEC verified". */
export function IdBadges({ badges }: { badges?: string[] }) {
  if (!badges?.length) return null;
  return <>{badges.map((b) => <Badge key={b} tone="good" title="Checked by the platform team"><BadgeCheck size={12} aria-hidden /> {b}</Badge>)}</>;
}

function StatusBadge({ i }: { i: Identifier }) {
  if (i.expired) return <Badge tone="bad">Expired</Badge>;
  const tone = i.status === 'rejected' ? 'bad' : i.verified ? 'good' : 'amber';
  return <Badge tone={tone}>{STATUS_LABEL[i.status]}</Badge>;
}

/** Read-only list for a company page (counterparties see verified and declared numbers, never documents). */
export function IdentifierList({ items }: { items: Identifier[] }) {
  if (!items.length) return <p className="muted small">No registration numbers published.</p>;
  return (
    <ul className="id-list">
      {items.map((i) => (
        <li key={i.id}>
          <span className="id-label">{i.label}</span>
          <span className="id-value mono">{i.value}</span>
          {i.issuer && <span className="tiny muted">{i.issuer}</span>}
          {i.valid_until && <span className="tiny muted">valid to {i.valid_until}</span>}
          <StatusBadge i={i} />
        </li>
      ))}
    </ul>
  );
}

/** Editable panel on My company (or staff editing any organisation). Staff also get review buttons. */
export function IdentifiersPanel({ orgRef, staff }: { orgRef: string; staff: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['identifiers', orgRef], queryFn: ({ signal }) => api.list(orgRef, signal) });
  const schemes = useSchemes(q.data?.country);
  const [adding, setAdding] = useState<string | null>(null);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['identifiers'] }); void qc.invalidateQueries({ queryKey: ['org'] }); };
  const del = useMutation({ mutationFn: (id: string) => api.remove(id), onSuccess: refresh });
  const review = useMutation({ mutationFn: (v: { id: string; status: IdentifierStatus }) => api.review(v.id, v.status), onSuccess: refresh });
  const view = q.data;
  const byCode = new Map((schemes.data?.schemes ?? []).map((s) => [s.code, s]));
  return (
    <Panel title="Business registrations" description="GSTIN, IEC, RCMC, GeM, BIS, EPR and similar numbers. Each is format-checked; the platform team checks it against the public register before a badge appears."
      action={<button type="button" className="button secondary sm" onClick={() => setAdding('')}><Plus size={14} aria-hidden />Add registration</button>}>
      <span id="registrations" />
      <ErrorNotice error={q.error ?? del.error ?? review.error} />
      {view?.missing.map((m) => (
        <Notice key={m.scheme} tone="warn"><ShieldAlert size={14} aria-hidden /> <strong>{m.label}</strong> is missing. {m.reason}{' '}
          <button type="button" className="button ghost sm" onClick={() => setAdding(m.scheme)}>Add it</button></Notice>
      ))}
      {view?.warnings.map((w) => <Notice key={w} tone="warn">{w}</Notice>)}
      {q.isLoading ? <Loading label="Loading registrations…" /> : !view?.identifiers.length ? (
        <Empty title="No registrations yet">Add your company's registration numbers so buyers and sellers can trust who they deal with.</Empty>
      ) : (
        <ul className="item-list">
          {view.identifiers.map((i) => (
            <li key={i.id}>
              <div className="grow">
                <div className="row"><strong>{i.label}</strong><span className="mono">{i.value}</span><StatusBadge i={i} /></div>
                <div className="tiny muted">
                  {[i.issuer, i.valid_until && `valid to ${i.valid_until}`, i.review_note && `Note: ${i.review_note}`].filter(Boolean).join(' · ')}
                  {i.document_url && <> · <a href={i.document_url} target="_blank" rel="noopener noreferrer">Certificate</a></>}
                </div>
              </div>
              <div className="row nowrap">
                {staff && i.lookup_url && <a className="button ghost sm" href={i.lookup_url} target="_blank" rel="noopener noreferrer"><ExternalLink size={13} aria-hidden />Check</a>}
                {staff && (
                  <select aria-label={`Review ${i.label}`} value={i.status} onChange={(e) => review.mutate({ id: i.id, status: e.target.value as IdentifierStatus })}>
                    {(Object.keys(STATUS_LABEL) as IdentifierStatus[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                )}
                <button type="button" className="icon-button" aria-label={`Delete ${i.label}`} onClick={() => { if (confirm(`Delete ${i.label}?`)) del.mutate(i.id); }}><Trash2 size={15} /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {adding !== null && view && (
        <IdentifierModal orgRef={orgRef} initial={adding} schemes={(schemes.data?.schemes ?? []).filter((s) => !view.identifiers.some((i) => i.scheme === s.code))}
          councils={schemes.data?.rcmc_councils ?? []} byCode={byCode} onClose={() => setAdding(null)} onSaved={refresh} />
      )}
    </Panel>
  );
}

function IdentifierModal({ orgRef, initial, schemes, councils, byCode, onClose, onSaved }: {
  orgRef: string; initial: string; schemes: IdentifierScheme[]; councils: string[]; byCode: Map<string, IdentifierScheme>; onClose: () => void; onSaved: () => void;
}) {
  const [f, setF] = useState<IdentifierInput>({ scheme: initial || schemes[0]?.code || '', value: '', issuer: '', valid_until: null, document_url: '' });
  const scheme = byCode.get(f.scheme);
  const save = useMutation({ mutationFn: () => api.add(orgRef, { ...f, valid_until: f.valid_until || null }), onSuccess: () => { onSaved(); onClose(); } });
  return (
    <Modal title="Add a registration" onClose={onClose}>
      <form className="form-stack" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <ErrorNotice error={save.error} />
        <Field label="Type">
          <select value={f.scheme} onChange={(e) => setF({ ...f, scheme: e.target.value, issuer: '' })} required>
            {schemes.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Number" hint={scheme ? `${scheme.hint}${scheme.example ? ` — e.g. ${scheme.example}` : ''}. Issued by ${scheme.issuer}.` : undefined}>
          <input type="text" required value={f.value} autoComplete="off" onChange={(e) => setF({ ...f, value: e.target.value })} />
        </Field>
        {scheme?.needs_issuer && (
          <Field label={f.scheme === 'rcmc' ? 'Export Promotion Council' : 'Issued by'}>
            {f.scheme === 'rcmc' ? (
              <select required value={f.issuer} onChange={(e) => setF({ ...f, issuer: e.target.value })}>
                <option value="">Choose…</option>{councils.map((c) => <option key={c}>{c}</option>)}
              </select>
            ) : <input type="text" required placeholder="e.g. Gujarat Pollution Control Board" value={f.issuer ?? ''} onChange={(e) => setF({ ...f, issuer: e.target.value })} />}
          </Field>
        )}
        {scheme?.needs_expiry && (
          <Field label="Valid until" hint="The badge disappears after this date; we remind you before it expires.">
            <input type="date" value={f.valid_until ?? ''} onChange={(e) => setF({ ...f, valid_until: e.target.value })} />
          </Field>
        )}
        <Field label="Certificate link" hint="Optional. Visible only to your company and the platform team.">
          <input type="url" placeholder="https://…" value={f.document_url ?? ''} onChange={(e) => setF({ ...f, document_url: e.target.value })} />
        </Field>
        <div className="modal-actions"><button type="button" className="button secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="button primary" disabled={save.isPending}>Save</button></div>
      </form>
    </Modal>
  );
}

/** Registration form fields: the country's primary ID (required) and, for Indian importers/exporters, the IEC. */
export function RegistrationIdFields({ country, value, onChange }: {
  country: string;
  value: { primary: string; iec: string; trades: boolean };
  onChange: (v: { primary: string; iec: string; trades: boolean }) => void;
}) {
  const schemes = useSchemes();
  const primaryCode = schemes.data?.primary_by_country[country] ?? 'company_reg';
  const primary = schemes.data?.schemes.find((s) => s.code === primaryCode);
  if (!country) return null;
  return (
    <>
      <Field label={primary?.label ?? 'Company registration number'} hint={primary ? `${primary.hint}. Required. We check it before your account goes live.` : 'Required.'}>
        <input type="text" required autoComplete="off" value={value.primary} placeholder={primary?.example || ''} onChange={(e) => onChange({ ...value, primary: e.target.value })} />
      </Field>
      {country === 'IN' && (
        <>
          <label className="checkbox"><input type="checkbox" checked={value.trades} onChange={(e) => onChange({ ...value, trades: e.target.checked })} />
            <span>We import or export (an IEC is required)</span></label>
          {value.trades && (
            <Field label="IEC (Importer-Exporter Code)" hint="10 characters; since 2020 the same as your company PAN.">
              <input type="text" required autoComplete="off" value={value.iec} onChange={(e) => onChange({ ...value, iec: e.target.value })} />
            </Field>
          )}
        </>
      )}
    </>
  );
}

export function registrationIdentifiers(primaryScheme: string, v: { primary: string; iec: string; trades: boolean }): IdentifierInput[] {
  const out: IdentifierInput[] = [];
  if (v.primary.trim()) out.push({ scheme: primaryScheme, value: v.primary.trim() });
  if (v.trades && v.iec.trim()) out.push({ scheme: 'iec', value: v.iec.trim() });
  return out;
}
